import React, { useState, useEffect, useRef } from 'react';
import { X, Printer, Mail, MessageCircle, Download, Loader2 } from 'lucide-react';
import { generateReceipt, generateReceiptImage, emailReceipt } from '../../api/receipts';
import { toast } from 'react-toastify';
import WhatsAppSendModal from './WhatsAppSendModal';
import { getCachedReceiptImage, cacheReceiptImage } from '../../utils/receiptImageCache';

/**
 * ReceiptPreviewModal Component
 * Displays a receipt PDF rendered as an image using PDF.js
 * The backend returns PDF, and we render it client-side to avoid server dependencies
 */
const ReceiptPreviewModal = ({
  saleId,
  receiptNumber,
  customerName,
  customerPhone,
  customerEmail,
  totalAmount,
  saleDate,
  onClose
}) => {
  const [receiptImageUrl, setReceiptImageUrl] = useState(null);
  const [loadingImage, setLoadingImage] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const canvasRef = useRef(null);
  const pdfLibRef = useRef(null); // Cache PDF.js library for performance

  // Email modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailMessage, setEmailMessage] = useState('');

  // WhatsApp modal state
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);

  useEffect(() => {
    if (saleId) {
      loadReceiptAsPdf();
    }
    // Note: We don't revoke blob URLs on unmount since they're cached
    // The cache will handle cleanup when entries expire
  }, [saleId]);

  /**
   * Load receipt PDF and render to canvas as image using dynamic import
   */
  const loadReceiptAsPdf = async () => {
    if (!saleId) {
      console.error('ReceiptPreviewModal: No saleId provided');
      setLoadError('No sale ID provided');
      setLoadingImage(false);
      return;
    }

    // Check cache first
    const cachedImage = getCachedReceiptImage(saleId);
    if (cachedImage) {
      console.log(`[CACHE HIT] Receipt ${saleId} loaded from cache`);
      setReceiptImageUrl(cachedImage);
      setLoadingImage(false);
      return;
    }

    console.log(`[CACHE MISS] Rendering receipt ${saleId}...`);
    setLoadingImage(true);
    setLoadError(null);

    try {
      console.log('ReceiptPreviewModal: Loading PDF for saleId:', saleId);

      // Fetch the PDF blob from the backend instead of image to avoid server-side dependency issues
      const blob = await generateReceipt(saleId, false);
      console.log('ReceiptPreviewModal: Received blob:', blob?.size, 'bytes, type:', blob?.type);

      if (!blob || blob.size === 0) {
        throw new Error('Empty response from server');
      }

      // Check if the blob is actually a PDF
      if (blob.type && !blob.type.includes('pdf')) {
        // If it's already an image, use it directly (fallback)
        if (blob.type.includes('image')) {
          const imageUrl = URL.createObjectURL(blob);
          setReceiptImageUrl(imageUrl);
          setLoadingImage(false);
          return;
        }
      }

      // Dynamic import PDF.js with caching to avoid repeated imports
      const importStart = Date.now();
      if (!pdfLibRef.current) {
        const pdfjsLib = await import('pdfjs-dist');
        // Use CDN for worker (reliable across all environments)
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
        pdfLibRef.current = pdfjsLib;
        console.log(`ReceiptPreviewModal: PDF.js loaded in ${Date.now() - importStart}ms`);
      }
      const pdfjsLib = pdfLibRef.current;

      // Convert blob to ArrayBuffer for PDF.js
      const arrayBuffer = await blob.arrayBuffer();

      // Load the PDF document
      const loadingTask = pdfjsLib.getDocument({
        data: arrayBuffer,
        isEvalSupported: false,
        useSystemFonts: true
      });
      const pdfDoc = await loadingTask.promise;

      console.log('ReceiptPreviewModal: PDF loaded, pages:', pdfDoc.numPages);

      // Get the first page
      const page = await pdfDoc.getPage(1);

      // Set scale to 1.0x for fastest rendering (still sharp on most displays)
      const scale = 1.0;
      const viewport = page.getViewport({ scale });

      // Create a canvas element
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Render the PDF page to canvas
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      const renderStart = Date.now();
      await page.render(renderContext).promise;
      console.log(`ReceiptPreviewModal: PDF rendered in ${Date.now() - renderStart}ms`);

      // Convert canvas to JPEG blob URL (much faster than PNG base64)
      const convertStart = Date.now();
      const imageBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
      const imageUrl = URL.createObjectURL(imageBlob);
      console.log(`ReceiptPreviewModal: Image converted in ${Date.now() - convertStart}ms`);

      // Cache the rendered image for future use
      cacheReceiptImage(saleId, imageUrl);

      setReceiptImageUrl(imageUrl);

      // Clean up
      await pdfDoc.destroy();
    } catch (error) {
      console.error('ReceiptPreviewModal: Error loading receipt:', error);
      setLoadError(error.message || 'Failed to load receipt');
      toast.error('Failed to load receipt preview: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingImage(false);
    }
  };

  const handleDownload = async () => {
    setActionLoading('download');
    try {
      const blob = await generateReceipt(saleId, true);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt-${receiptNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded successfully');
    } catch (error) {
      toast.error('Failed to download receipt');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePrint = async () => {
    if (!receiptImageUrl) return;

    try {
      let printImageUrl = receiptImageUrl;

      // If it's a blob URL, we need to convert it to a data URL
      // because blob URLs are not accessible from other windows
      if (receiptImageUrl.startsWith('blob:')) {
        const response = await fetch(receiptImageUrl);
        const blob = await response.blob();
        printImageUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      }

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Receipt - ${receiptNumber}</title>
              <style>
                body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
                img { max-width: 100%; height: auto; }
                @media print {
                  body { margin: 0; }
                  img { max-width: 100%; }
                }
              </style>
              <script>
                function doPrint() {
                  window.print();
                  // Close after print dialog is dismissed (user might cancel)
                  setTimeout(function() {
                    window.close();
                  }, 100);
                }
              </script>
            </head>
            <body>
              <img src="${printImageUrl}" onload="doPrint();" onerror="console.error('Image failed to load');" />
            </body>
          </html>
        `);
        printWindow.document.close();
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to prepare receipt for printing');
    }
  };

  const handleWhatsApp = () => {
    setWhatsAppModalOpen(true);
  };

  const handleEmailClick = () => {
    setEmailAddress(customerEmail || '');
    setEmailMessage('');
    setEmailModalOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailAddress) {
      toast.error('Please enter an email address');
      return;
    }

    setActionLoading('email');
    try {
      await emailReceipt(saleId, emailAddress, emailMessage);
      toast.success('Receipt sent successfully');
      setEmailModalOpen(false);
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetry = () => {
    loadReceiptAsPdf();
  };

  return (
    <>
      {/* Main Receipt Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-bold text-primary-black">Receipt #{receiptNumber}</h2>
              <p className="text-sm text-secondary-gray">
                {customerName} • {new Date(saleDate).toLocaleDateString()}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Modal Body - Receipt Image */}
          <div className="flex-1 overflow-auto p-4 bg-gray-50">
            {loadingImage ? (
              <div className="flex flex-col items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-accent-gold animate-spin mb-3" />
                <p className="text-sm text-gray-500">Loading receipt...</p>
              </div>
            ) : receiptImageUrl ? (
              <div className="flex justify-center">
                <img
                  src={receiptImageUrl}
                  alt={`Receipt ${receiptNumber}`}
                  className="max-w-full h-auto shadow-lg rounded-lg"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-secondary-gray">
                <p className="mb-2">Failed to load receipt preview</p>
                {loadError && <p className="text-xs text-red-500 mb-3">{loadError}</p>}
                <button
                  onClick={handleRetry}
                  className="px-4 py-2 text-sm bg-accent-gold text-white rounded-lg hover:bg-accent-gold/90"
                >
                  Retry
                </button>
              </div>
            )}
          </div>

          {/* Modal Footer - Action Buttons */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <button
                onClick={handlePrint}
                disabled={!receiptImageUrl}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Printer className="w-4 h-4" />
                <span className="text-sm font-medium">Print</span>
              </button>
              <button
                onClick={handleWhatsApp}
                disabled={!receiptImageUrl}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm font-medium">WhatsApp</span>
              </button>
              <button
                onClick={handleEmailClick}
                disabled={actionLoading === 'email'}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Mail className="w-4 h-4" />
                <span className="text-sm font-medium">Email</span>
              </button>
              <button
                onClick={handleDownload}
                disabled={actionLoading === 'download'}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-accent-gold text-white rounded-lg hover:bg-accent-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading === 'download' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="text-sm font-medium">Download</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {emailModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-primary-black">Send Receipt via Email</h2>
              <button
                onClick={() => setEmailModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="customer@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Message (Optional)
                </label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Add a personal message..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-200">
              <button
                onClick={() => setEmailModalOpen(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={actionLoading === 'email'}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading === 'email' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4" />
                )}
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {whatsAppModalOpen && (
        <WhatsAppSendModal
          sale={{
            id: saleId,
            receipt_number: receiptNumber,
            customer_name: customerName,
            customer_phone: customerPhone,
            total_amount: totalAmount
          }}
          onClose={() => setWhatsAppModalOpen(false)}
          onSuccess={() => {
            toast.success('Receipt sent via WhatsApp');
          }}
        />
      )}
    </>
  );
};

export default ReceiptPreviewModal;
